import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Shield, BarChart3, Megaphone, Settings } from "lucide-react";
import { useCookieConsent } from "@/hooks/useCookieConsent";

export function CookieConsent() {
  const {
    consent,
    showBanner,
    showSettings,
    acceptAll,
    acceptNecessaryOnly,
    saveConsent,
    openSettings,
    closeSettings,
  } = useCookieConsent();

  const [analyticsChecked, setAnalyticsChecked] = useState(consent?.analytics ?? false);
  const [marketingChecked, setMarketingChecked] = useState(consent?.marketing ?? false);

  const handleOpenSettings = () => {
    setAnalyticsChecked(consent?.analytics ?? false);
    setMarketingChecked(consent?.marketing ?? false);
    openSettings();
  };

  const handleSaveSettings = () => {
    saveConsent({ analytics: analyticsChecked, marketing: marketingChecked });
  };

  return (
    <>
      {/* Bottom Banner */}
      {showBanner && (
        <div className="fixed bottom-0 left-0 right-0 z-[9999] bg-[hsl(var(--fog))] border-t-2 border-[hsl(var(--blue))] shadow-elevated animate-in slide-in-from-bottom duration-300 max-h-[85dvh] overflow-y-auto">
          <div
            className="container mx-auto px-4 pt-3 md:py-5"
            style={{ paddingBottom: "max(12px, var(--safe-bottom,0px))" }}
          >
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div className="flex-1 min-w-0">
                <p className="text-small text-foreground">
                  Wir verwenden Cookies für ein optimales Erlebnis. Du entscheidest, welche wir nutzen dürfen.{" "}
                  <Link to="/datenschutz" className="text-foreground underline underline-offset-4">
                    Datenschutzerklärung
                  </Link>
                </p>
              </div>
              <div className="grid grid-cols-2 gap-2 sm:flex sm:items-center sm:gap-3 shrink-0">
                <Button
                  size="sm"
                  onClick={acceptAll}
                  className="col-span-2 order-first sm:order-last h-11 sm:h-9 text-[15px] sm:text-sm font-bold bg-[#644B40] text-[#F3EFE8] hover:bg-[#7ECAEE] hover:text-[#644B40]"
                >
                  Alle akzeptieren
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={acceptNecessaryOnly}
                  className="h-10 sm:h-9 border-foreground bg-transparent text-foreground hover:bg-foreground hover:text-[hsl(var(--white))]"
                >
                  Nur notwendige
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleOpenSettings}
                  className="h-10 sm:h-9 gap-1.5 text-foreground hover:bg-foreground/10"
                >
                  <Settings className="w-4 h-4" />
                  Einstellungen
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Settings Modal */}
      <CookieSettingsModal
        open={showSettings}
        onOpenChange={(open) => { if (!open) closeSettings(); }}
        analyticsChecked={analyticsChecked}
        setAnalyticsChecked={setAnalyticsChecked}
        marketingChecked={marketingChecked}
        setMarketingChecked={setMarketingChecked}
        onSave={handleSaveSettings}
        onAcceptAll={() => {
          setAnalyticsChecked(true);
          setMarketingChecked(true);
          saveConsent({ analytics: true, marketing: true });
        }}
      />
    </>
  );
}

/** Exported so Footer can open it independently */
export function CookieSettingsModal({
  open,
  onOpenChange,
  analyticsChecked,
  setAnalyticsChecked,
  marketingChecked,
  setMarketingChecked,
  onSave,
  onAcceptAll,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  analyticsChecked: boolean;
  setAnalyticsChecked: (v: boolean) => void;
  marketingChecked: boolean;
  setMarketingChecked: (v: boolean) => void;
  onSave: () => void;
  onAcceptAll: () => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-title">Cookie-Einstellungen</DialogTitle>
          <DialogDescription>
            Wähle aus, welche Cookies du zulassen möchtest.{" "}
            <Link to="/datenschutz" className="text-primary hover:underline">
              Mehr erfahren
            </Link>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Necessary */}
          <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
            <Shield className="w-5 h-5 text-primary mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <span className="text-body">Notwendig</span>
                <Switch checked disabled className="opacity-60" />
              </div>
              <p className="text-small text-muted-foreground mt-1">
                Diese Cookies sind für die Grundfunktionen der Website erforderlich und können nicht deaktiviert werden.
              </p>
            </div>
          </div>

          {/* Analytics */}
          <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
            <BarChart3 className="w-5 h-5 text-primary mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <span className="text-body">Statistik / Analyse</span>
                <Switch checked={analyticsChecked} onCheckedChange={setAnalyticsChecked} />
              </div>
              <p className="text-small text-muted-foreground mt-1">
                Helfen uns zu verstehen, wie Besucher unsere Website nutzen: eigene Messung mit Besucherkennung und Kampagnen-Herkunft, A/B-Tests, Google Analytics, Microsoft Clarity, Contentsquare.
              </p>
            </div>
          </div>

          {/* Marketing */}
          <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
            <Megaphone className="w-5 h-5 text-primary mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <span className="text-body">Marketing</span>
                <Switch checked={marketingChecked} onCheckedChange={setMarketingChecked} />
              </div>
              <p className="text-small text-muted-foreground mt-1">
                Ordnen Anfragen und Käufe unseren Anzeigen bei Google und Meta zu und machen Werbung relevanter (Meta Pixel, Google Ads).
              </p>
            </div>
          </div>
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-row">
          <Button variant="outline" onClick={onSave} className="w-full sm:w-auto">
            Auswahl speichern
          </Button>
          <Button onClick={onAcceptAll} className="w-full sm:w-auto">
            Alle akzeptieren
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
